﻿import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionBillingCycle } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  name: string;
  code: string;
  interval_months: string;
  interval_days: string;
  is_recurring: boolean;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  cycle?: SubscriptionBillingCycle;
  onClose: () => void;
  onSuccess: () => void;
}

export function BillingCycleFormDialog({ open, mode, cycle, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', code: '', interval_months: '', interval_days: '', is_recurring: true },
  });

  const isRecurring = watch('is_recurring');
  const name = watch('name');

  useEffect(() => {
    if (!isEdit) {
      setValue('code', name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''));
    }
  }, [name, isEdit, setValue]);

  useEffect(() => {
    if (open) {
      reset({
        name: cycle?.name ?? '',
        code: cycle?.code ?? '',
        interval_months: cycle?.interval_months != null ? String(cycle.interval_months) : '',
        interval_days: cycle?.interval_days != null ? String(cycle.interval_days) : '',
        is_recurring: cycle?.is_recurring ?? true,
      });
    }
  }, [open, cycle, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      const input = {
        name: v.name.trim(),
        code: v.code.trim(),
        interval_months: v.interval_months ? parseInt(v.interval_months, 10) : undefined,
        interval_days: v.interval_days ? parseInt(v.interval_days, 10) : undefined,
        is_recurring: v.is_recurring,
      };
      return isEdit && cycle
        ? subscriptionService.updateBillingCycle(cycle.id, input)
        : subscriptionService.createBillingCycle(input);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Billing cycle updated' : 'Billing cycle created');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'billing-cycles'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Billing Cycle' : 'New Billing Cycle'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="bc-name">Name *</Label>
              <Input id="bc-name" {...register('name', { required: 'Name is required' })} placeholder="e.g. Quarterly" />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="bc-code">Code *</Label>
              <Input id="bc-code" {...register('code', { required: 'Code is required' })} placeholder="e.g. quarterly" />
              {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-months">Interval Months</Label>
              <Input id="bc-months" type="number" min={0} {...register('interval_months')} placeholder="e.g. 3" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-days">Interval Days</Label>
              <Input id="bc-days" type="number" min={0} {...register('interval_days')} placeholder="e.g. 90" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="bc-recurring"
              checked={isRecurring}
              onChange={(e) => setValue('is_recurring', e.target.checked)}
            />
            <Label htmlFor="bc-recurring" className="cursor-pointer font-normal">
              Recurring cycle
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
